import { useCallback, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { ChevronRight, Search } from 'lucide-react';
import { formatCurrency } from '@/lib/i18n';
import { api } from '@/lib/api';
import { useCurrency } from '@/Hooks/useCurrency';
import { usePermission } from '@/Hooks/usePermission';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge, toneForStatus } from '@/components/ui/badge';
import { Card, CardContent } from '@/components/ui/card';
import { Dialog } from '@/components/ui/dialog';
import { Select } from '@/components/ui/field';
import { EmptyState, ErrorState, LoadingState } from '@/components/ui/states';
import CheckDetail from './CheckDetail';
import ShiftStrip from './ShiftPanel';
import { formatClock } from './clock';
import {
  CHECK_STATUSES,
  type CashierShift,
  type Check,
  apiError,
  balanceOf,
  fromMinor,
  toMinor,
} from './lib';

interface Branch {
  id: string;
  name: string;
}

/** The one check whose bill is on screen. Its number is kept beside its id so
 *  the dialog keeps its title even when a reload drops the row out of the
 *  current filter — settling a check is exactly what makes it stop matching
 *  `status: OPEN`. */
interface OpenCheck {
  id: string;
  number: string;
}

/**
 * P4-05 / P9-02 / P9-03 — the till and the day's record.
 *
 * The screen used to do three unrelated jobs in two columns: run the cashier
 * drawer, list the day's checks, and settle one of them. The drawer sat *under*
 * a filtered list even though which row is selected has nothing to do with how
 * much cash is in it, and a second, differently-shaped settle surface competed
 * with the one the floor map already opens for a table.
 *
 * So there are three bands now, in the order the work happens:
 *
 *  1. `ShiftStrip` — the drawer as one line of status across the top, with its
 *     cash movements, its figures and its close-and-count behind buttons.
 *  2. the day's totals, then one compact row of filters.
 *  3. the list, full width — and a row opens `CheckDetail` in a dialog, the
 *     same component the floor map's Bill pane mounts, so settling a bill looks
 *     the same wherever it is reached from.
 */
export default function Checks() {
  const { t, i18n } = useTranslation();
  const { can } = usePermission();

  /**
   * `?sessionId=` — arriving from a table on the floor map means "this table's
   * bill", not "every open check in the venue". The status filter is dropped
   * with it, so a check already paid still shows.
   */
  const [searchParams, setSearchParams] = useSearchParams();
  const sessionId = searchParams.get('sessionId');

  const [rows, setRows] = useState<Check[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [status, setStatus] = useState<string>('');
  const [branchId, setBranchId] = useState('');
  const [branches, setBranches] = useState<Branch[]>([]);
  const [open, setOpen] = useState<OpenCheck | null>(null);
  const [shift, setShift] = useState<CashierShift | null>(null);
  /** Bumped when a payment, refund or tip lands, so the drawer strip re-reads.
   *  Typing in the search box is not money moving, and does not bump it. */
  const [moneyMoved, setMoneyMoved] = useState(0);

  /**
   * The day this screen is reporting on, today by default.
   *
   * Settling a table's bill now happens on the table, so what is left here is
   * the till — walk-in and takeaway checks, which have no table to be settled
   * from — and the day's record: what was taken, what is still owing, and a
   * reprint. A list that reached back over every check ever opened answered
   * neither question well.
   */
  const [day, setDay] = useState(() => new Date().toLocaleDateString('en-CA'));

  const load = useCallback(() => {
    setLoading(true);
    setError(null);
    api
      .get<{ data: Check[] }>('/checks', {
        params: {
          limit: 100,
          search: search || undefined,
          status: status || undefined,
          branchId: branchId || undefined,
          sessionId: sessionId || undefined,
          // A session link asks about one visit wherever it falls, so the day
          // window is dropped for it rather than hiding yesterday's late table.
          ...(sessionId || !day
            ? {}
            : {
                from: new Date(`${day}T00:00:00`).toISOString(),
                to: new Date(`${day}T23:59:59.999`).toISOString(),
              }),
        },
      })
      .then(({ data }) => setRows(data.data ?? []))
      .catch((e) => setError(apiError(e, t('billing.checks.loadFailed'))))
      .finally(() => setLoading(false));
  }, [search, status, branchId, sessionId, day, t]);

  useEffect(() => {
    const timer = setTimeout(load, 250);
    return () => clearTimeout(timer);
  }, [load]);

  useEffect(() => {
    // `branches.view` gates /branches (D-05: it is NOT `settings.view`); a
    // caller may still lack it, so fall back to the branches the checks name.
    api
      .get<{ data: Branch[] }>('/branches', { params: { limit: 100 } })
      .then(({ data }) => setBranches(data.data ?? []))
      .catch(() => setBranches([]));
  }, []);

  const branchOptions = useMemo(() => {
    if (branches.length > 0) return branches;
    return [...new Set(rows.map((row) => row.branchId))].map((id) => ({ id, name: id.slice(-6) }));
  }, [branches, rows]);

  // The drawer belongs to a branch; use the filter, else the branch of the bill
  // being settled, else the first branch this cashier can see.
  const shiftBranchId =
    branchId || rows.find((row) => row.id === open?.id)?.branchId || branchOptions[0]?.id || null;

  // The drawer and the headline totals belong to a branch, so they are read in
  // that branch's currency rather than whichever check happened to sort first.
  const { code: currency, format: formatMoney } = useCurrency(shiftBranchId);

  const totals = useMemo(() => {
    // With no branch filter the list can span branches, and adding a dinar to a
    // riyal is meaningless — only rows already in `currency` are summed.
    let grand = 0;
    let paid = 0;
    for (const row of rows) {
      if (row.currency !== currency) continue;
      grand += toMinor(row.grandTotal, currency);
      paid += toMinor(row.paidTotal, currency);
    }
    return {
      grand: fromMinor(grand, currency),
      paid: fromMinor(paid, currency),
      due: fromMinor(grand - paid, currency),
    };
  }, [rows, currency]);

  /** A bill changed — reload the list, and tell the drawer strip to re-read. */
  const afterChange = useCallback(() => {
    setMoneyMoved((n) => n + 1);
    load();
  }, [load]);

  /**
   * A walk-in, a takeaway or a bar tab has no table to be settled from, so this
   * is the only place in the product that opens one. It lands straight in the
   * settle dialog, because opening a check is never the goal — taking the money
   * on it is.
   */
  const openWalkIn = async () => {
    if (!shiftBranchId) return;
    try {
      const { data } = await api.post<Check>('/checks', { branchId: shiftBranchId });
      toast.success(t('billing.checks.opened', { number: data.number }));
      setOpen({ id: data.id, number: data.number });
      load();
    } catch (e) {
      toast.error(apiError(e, t('billing.checks.openFailed')));
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold">{t('nav.checks')}</h1>
          <p className="text-sm text-ink-muted">
            {t('billing.checks.subtitleDay')}
          </p>
        </div>
        {can('checks.create') && (
          <Button size="lg" onClick={openWalkIn} disabled={!shiftBranchId}>
            {t('billing.checks.openWalkIn')}
          </Button>
        )}
      </div>

      {/* One fact and a few actions, across the top: the drawer is a property of
          the branch, not of whichever row is selected below. */}
      <ShiftStrip
        branchId={shiftBranchId}
        currency={currency}
        refreshKey={moneyMoved}
        onShiftChange={setShift}
      />

      <div className="grid gap-3 sm:grid-cols-3">
        <Stat label={t('billing.checks.totalBilled')} value={formatMoney(totals.grand)} />
        <Stat label={t('billing.checks.totalPaid')} value={formatMoney(totals.paid)} />
        <Stat
          label={t('billing.checks.totalOutstanding')}
          value={formatMoney(totals.due)}
          tone={toMinor(totals.due, currency) > 0 ? 'text-amber-700 dark:text-amber-400' : 'text-green-700 dark:text-green-400'}
        />
      </div>

      <Card>
        <CardContent className="space-y-3 p-4">
          <div className="flex flex-wrap items-center gap-2">
            <div className="relative min-w-48 flex-1">
              <Search
                className="pointer-events-none absolute inset-y-0 start-3 my-auto h-4 w-4 text-ink-muted"
                aria-hidden
              />
              <Input
                className="ps-9"
                placeholder={t('billing.checks.searchPlaceholder')}
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
            </div>

            {/* Narrowed to one table's visit — say so, and give a way back out,
                so an empty list never reads as "this venue has no checks". The
                day picker is not shown while it is on, because the session
                filter deliberately ignores it. */}
            {sessionId ? (
              <>
                <span className="inline-flex h-11 items-center rounded-lg border border-sky-200 dark:border-sky-900 bg-sky-50 dark:bg-sky-950/50 px-3 text-sm font-medium text-sky-800 dark:text-sky-300">
                  {t('billing.checks.filteredBySession')}
                </span>
                <Button
                  variant="outline"
                  onClick={() => {
                    searchParams.delete('sessionId');
                    setSearchParams(searchParams, { replace: true });
                    setStatus('OPEN');
                  }}
                >
                  {t('billing.checks.showAll')}
                </Button>
              </>
            ) : (
              <>
                <Input
                  type="date"
                  className="w-auto"
                  value={day}
                  onChange={(e) => setDay(e.target.value)}
                  aria-label={t('billing.checks.day')}
                />
                <Button
                  variant="secondary"
                  onClick={() => setDay(new Date().toLocaleDateString('en-CA'))}
                >
                  {t('billing.checks.today')}
                </Button>
              </>
            )}

            <Select
              className="w-auto"
              value={status}
              onChange={(e) => setStatus(e.target.value)}
              aria-label={t('common.status')}
            >
              <option value="">{t('billing.checks.allStatuses')}</option>
              {CHECK_STATUSES.map((value) => (
                <option key={value} value={value}>
                  {t(`billing.status.${value}`)}
                </option>
              ))}
            </Select>
            <Select
              className="w-auto"
              value={branchId}
              onChange={(e) => setBranchId(e.target.value)}
              aria-label={t('billing.checks.branch')}
            >
              <option value="">{t('billing.checks.allBranches')}</option>
              {branchOptions.map((branch) => (
                <option key={branch.id} value={branch.id}>
                  {branch.name}
                </option>
              ))}
            </Select>
          </div>

          {loading ? (
            <LoadingState label={t('common.loading')} />
          ) : error ? (
            <ErrorState message={error} onRetry={load} />
          ) : rows.length === 0 ? (
            <EmptyState
              title={t('billing.checks.empty')}
              hint={t('billing.checks.emptyHint')}
              action={
                can('checks.create') ? (
                  <Button onClick={openWalkIn} disabled={!shiftBranchId}>
                    {t('billing.checks.openWalkIn')}
                  </Button>
                ) : undefined
              }
            />
          ) : (
            <>
              <p className="text-xs text-ink-muted">
                {t('billing.checks.count', { count: rows.length })}
              </p>
              <ul className="divide-y overflow-hidden rounded-lg border">
                {rows.map((row) => {
                  const due = balanceOf(row);
                  const owing = toMinor(due, row.currency) > 0;
                  return (
                    <li key={row.id}>
                      {/* A whole row is the target — 56px of it, because this
                          runs on a till and nothing here is reachable by hover
                          alone. */}
                      <button
                        type="button"
                        onClick={() => setOpen({ id: row.id, number: row.number })}
                        aria-label={t('billing.checks.openCheck', { number: row.number })}
                        className="flex min-h-14 w-full flex-wrap items-center gap-x-4 gap-y-1 px-4 py-3 text-start hover:bg-hover focus-visible:bg-hover focus-visible:outline-none"
                      >
                        <span className="flex min-w-40 items-center gap-2">
                          <span className="font-medium">
                            {t('billing.checks.checkNumber', { number: row.number })}
                          </span>
                          <Badge tone={toneForStatus(row.status)}>
                            {t(`billing.status.${row.status}`)}
                          </Badge>
                          {row.isMaster && (
                            <Badge tone="brand">{t('billing.checks.master')}</Badge>
                          )}
                        </span>

                        <span className="min-w-0 flex-1 truncate text-sm text-ink-muted">
                          {row.session
                            ? row.session.table
                              ? t('billing.checks.tableLine', {
                                  number: row.session.table.number,
                                })
                              : t('billing.checks.visitLine')
                            : t('billing.checks.walkIn')}
                          {' · '}
                          {formatClock(row.openedAt, i18n.language)}
                        </span>

                        <span className="text-end text-sm tabular-nums">
                          <span className="block font-medium">
                            {formatCurrency(row.grandTotal, row.currency, i18n.language)}
                          </span>
                          <span
                            className={
                              owing
                                ? 'block text-xs text-amber-700 dark:text-amber-400'
                                : 'block text-xs text-ink-muted'
                            }
                          >
                            {owing
                              ? `${formatCurrency(due, row.currency, i18n.language)} ${t('billing.checks.due')}`
                              : t('billing.checks.settledShort')}
                          </span>
                        </span>

                        <ChevronRight
                          className="h-4 w-4 shrink-0 text-ink-muted rtl:rotate-180"
                          aria-hidden
                        />
                      </button>
                    </li>
                  );
                })}
              </ul>
            </>
          )}
        </CardContent>
      </Card>

      {/* The same `CheckDetail` the floor map's Bill pane mounts, in the same
          shape of container, so a bill settled from the till and a bill settled
          from a table are one surface rather than two. */}
      {open && (
        <Dialog
          open
          onClose={() => setOpen(null)}
          title={t('billing.checks.checkNumber', { number: open.number })}
          className="max-w-5xl"
        >
          <CheckDetail
            key={open.id}
            checkId={open.id}
            checks={rows}
            shift={shift}
            onChanged={afterChange}
          />
        </Dialog>
      )}
    </div>
  );
}

function Stat({ label, value, tone }: { label: string; value: string; tone?: string }) {
  return (
    <Card>
      <CardContent className="p-4">
        <p className="text-xs text-ink-muted">{label}</p>
        <p className={`text-xl font-semibold tabular-nums ${tone ?? ''}`}>{value}</p>
      </CardContent>
    </Card>
  );
}
